import axios from 'axios';

// Create an axios instance with default configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api',
  timeout: 10000,
});

// Add a request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers['Content-Type'] = 'application/json';
    console.log('API Request:', config.method?.toUpperCase(), config.url, config.params);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    // Handle success responses in the new format
    if (response.data && response.data.success === false) {
      return Promise.reject(new Error(response.data.message || 'An error occurred'));
    }
    console.log('API Response:', response.config.method?.toUpperCase(), response.config.url, response.status, response.data);
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized, redirect to login
      localStorage.removeItem('authToken');
      window.location.href = '/login';
    }
    console.error('API Error:', error.response?.status, error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// Auth endpoints
export const authAPI = {
  login: (email, password) => 
    api.post('/auth/login', { email, password }),
  logout: () => 
    api.post('/auth/logout'),
  refreshToken: () => 
    api.post('/auth/refresh'),
  // Add a method to get current user profile
  getCurrentUser: () => 
    api.get('/auth/me')
};

// Hotels endpoints (updated to use /api/dashboard/hotels)
export const hotelsAPI = {
  getAll: () => 
    api.get('/admin/hotels'),
  getById: (id) => 
    api.get(`/admin/hotels/${id}`),
  create: (hotelData) => 
    api.post('/admin/hotels', hotelData),
  update: (id, hotelData) => 
    api.put(`/admin/hotels/${id}`, hotelData),
  delete: (id) => 
    api.delete(`/admin/hotels/${id}`)
};

// Rooms endpoints (updated to use /api/admin/rooms)
export const roomsAPI = {
  getAll: () => 
    api.get('/admin/rooms'),
  getByHotelId: (id) => 
    api.get(`/admin/rooms?hotel_id=${id}`),
  getById: (id) => 
    api.get(`/admin/rooms/${id}`),
  create: (roomData) => 
    api.post('/admin/rooms', roomData),
  update: (id, roomData) => 
    api.put(`/admin/rooms/${id}`, roomData),
  delete: (id) => 
    api.delete(`/admin/rooms/${id}`)
};

// Travelomatix Room Search API (public access - no authentication required)
export const travelomatixRoomsAPI = {
  search: (searchParams) => 
    api.post('/travelomatix/rooms/search', searchParams)
};

// Bookings endpoints (updated to use /api/dashboard/bookings)
export const bookingsAPI = {
  getAll: () => 
    api.get('/bookings/admin/all'),
  getById: (id) => 
    api.get(`/dashboard/bookings/${id}`),
  create: (bookingData) => 
    api.post('/dashboard/bookings', bookingData),
  update: (id, bookingData) => 
    api.put(`/dashboard/bookings/${id}`, bookingData),
  delete: (id) => 
    api.delete(`/dashboard/bookings/${id}`)
};

// Users endpoints (updated to use /api/dashboard/users)
export const usersAPI = {
  getAll: () => 
    api.get('/dashboard/users'),
  getById: (id) => 
    api.get(`/dashboard/users/${id}`),
  create: (userData) => 
    api.post('/dashboard/users', userData),
  update: (id, userData) => 
    api.put(`/dashboard/users/${id}`, userData),
  delete: (id) => 
    api.delete(`/dashboard/users/${id}`)
};

// Coupons endpoints (updated to use /api/coupons)
export const couponsAPI = {
  getAll: () => 
    api.get('/coupons'),
  getById: (id) => 
    api.get(`/coupons/${id}`),
  create: (couponData) => {
    // Map frontend field names to backend database field names
    const backendData = {
      code: couponData.code,
      discount_type: couponData.discount_type,
      discount_value: parseFloat(couponData.discount_value),
      valid_from: couponData.valid_from,
      valid_until: couponData.valid_until,
      max_uses: couponData.max_uses ? parseInt(couponData.max_uses) : undefined,
      min_amount: couponData.min_amount ? parseFloat(couponData.min_amount) : undefined,
      user_id: couponData.user_id,
      status: couponData.status
    };
    
    // Remove undefined fields
    Object.keys(backendData).forEach(key => {
      if (backendData[key] === undefined) {
        delete backendData[key];
      }
    });
    
    console.log('Sending coupon data to backend:', backendData);
    return api.post('/coupons', backendData)
      .catch(error => {
        console.error('Coupon creation error:', error.response?.data || error.message);
        throw error;
      });
  },
  update: (id, couponData) => {
    // Map frontend field names to backend database field names
    const backendData = {
      discount_type: couponData.discount_type,
      discount_value: parseFloat(couponData.discount_value),
      valid_from: couponData.valid_from,
      valid_until: couponData.valid_until,
      max_uses: couponData.max_uses ? parseInt(couponData.max_uses) : undefined,
      min_amount: couponData.min_amount ? parseFloat(couponData.min_amount) : undefined,
      user_id: couponData.user_id,
      status: couponData.status
    };
    
    // Remove undefined fields
    Object.keys(backendData).forEach(key => {
      if (backendData[key] === undefined) {
        delete backendData[key];
      }
    });
    
    console.log('Updating coupon data:', backendData);
    return api.put(`/coupons/${id}`, backendData)
      .catch(error => {
        console.error('Coupon update error:', error.response?.data || error.message);
        throw error;
      });
  },
  delete: (id) => 
    api.delete(`/coupons/${id}`)
};

// Transactions endpoints (updated to use /api/dashboard/transactions)
export const transactionsAPI = {
  getAll: () => 
    api.get('/payment/admin/transactions'),
  getById: (id) => 
    api.get(`/dashboard/transactions/${id}`)
};

// Reports endpoints - Updated to match actual backend endpoints
export const reportsAPI = {
  // User-facing dashboard reports
  getBookingsByCountry: (startDate, endDate) => {
    let url = '/dashboard/reports/bookings-by-country';
    if (startDate && endDate) {
      url += `?start=${startDate}&end=${endDate}`;
    }
    console.log('API Call: getBookingsByCountry', url);
    return api.get(url);
  },
  getRevenueByHotel: (startDate, endDate) => {
    let url = '/dashboard/reports/revenue-by-hotel';
    if (startDate && endDate) {
      url += `?start=${startDate}&end=${endDate}`;
    }
    console.log('API Call: getRevenueByHotel', url);
    return api.get(url);
  },
  getBookingTrends: (startDate, endDate) => {
    let url = '/dashboard/reports/booking-trends';
    if (startDate && endDate) {
      url += `?start=${startDate}&end=${endDate}`;
    }
    console.log('API Call: getBookingTrends', url);
    return api.get(url);
  },
  getUserDemographics: (startDate, endDate) => {
    let url = '/dashboard/reports/user-demographics';
    if (startDate && endDate) {
      url += `?start=${startDate}&end=${endDate}`;
    }
    console.log('API Call: getUserDemographics', url);
    return api.get(url);
  },
  // Admin overview endpoints
  getAdminOverview: () => {
    console.log('API Call: getAdminOverview');
    return api.get('/admin/overview');
  },
  getAdminStats: () => {
    console.log('API Call: getAdminStats');
    return api.get('/admin/stats/dashboard');
  },
  getSuperAdminStats: () => {
    console.log('API Call: getSuperAdminStats');
    return api.get('/admin/stats/advanced');
  },
  // Visitor Analytics endpoints (NEW)
  getVisitorAnalytics: (params = {}) => {
    const { period, startDate, endDate } = params;
    let url = '/admin/reports/visitor-analytics';
    const queryParams = [];
    
    if (period) queryParams.push(`period=${period}`);
    if (startDate) queryParams.push(`start_date=${startDate}`);
    if (endDate) queryParams.push(`end_date=${endDate}`);
    
    if (queryParams.length > 0) {
      url += `?${queryParams.join('&')}`;
    }
    
    console.log('API Call: getVisitorAnalytics', url, params);
    return api.get(url);
  },
  getVisitorTrends: (params = {}) => {
    const { period, startDate, endDate, interval } = params;
    let url = '/admin/reports/visitor-trends';
    const queryParams = [];
    
    if (period) queryParams.push(`period=${period}`);
    if (startDate) queryParams.push(`start_date=${startDate}`);
    if (endDate) queryParams.push(`end_date=${endDate}`);
    if (interval) queryParams.push(`interval=${interval}`);
    
    if (queryParams.length > 0) {
      url += `?${queryParams.join('&')}`;
    }
    
    console.log('API Call: getVisitorTrends', url, params);
    return api.get(url);
  },
  // Profit endpoint for super admin
  getProfit: (params = {}) => {
    const { month, year, hotel_id } = params;
    let url = '/admin/profit';
    const queryParams = [];
    
    if (month) queryParams.push(`month=${month}`);
    if (year) queryParams.push(`year=${year}`);
    if (hotel_id) queryParams.push(`hotel_id=${hotel_id}`);
    
    if (queryParams.length > 0) {
      url += `?${queryParams.join('&')}`;
    }
    
    console.log('API Call: getProfit', url, params);
    return api.get(url);
  }
};

// Accessibility Templates API
export const accessibilityAPI = {
  // Public endpoint (frontend use)
  getAllTemplates: () => 
    api.get('/admin/accessibility-template'),
  
  // Admin endpoints (dashboard use)
  getTemplateById: (id) => 
    api.get(`/admin/accessibility-templates/${id}`),
  
  createTemplate: (templateData) => 
    api.post('/admin/accessibility-templates', templateData),
  
  updateTemplate: (id, templateData) => 
    api.put(`/admin/accessibility-templates/${id}`, templateData),
  
  deleteTemplate: (id) => 
    api.delete(`/admin/accessibility-templates/${id}`)
};

// Amenities API
export const amenitiesAPI = {
  getAll: () => 
    api.get('/amenities'),
  getById: (id) => 
    api.get(`/amenities/${id}`),
  create: (amenityData) => 
    api.post('/amenities', {
      name_en: amenityData.name_en,
      name_ar: amenityData.name_ar,
      description_en: amenityData.description_en,
      description_ar: amenityData.description_ar,
      is_active: amenityData.is_active !== undefined ? amenityData.is_active : true
    }),
  update: (id, amenityData) => 
    api.put(`/amenities/${id}`, {
      name_en: amenityData.name_en,
      name_ar: amenityData.name_ar,
      description_en: amenityData.description_en,
      description_ar: amenityData.description_ar,
      is_active: amenityData.is_active
    }),
  delete: (id) => 
    api.delete(`/amenities/${id}`)  // This will deactivate the amenity
};

// Partners API
export const partnersAPI = {
  getAll: () => 
    api.get('/partners'),
  create: (partnerData) => 
    api.post('/partners', partnerData),
  update: (id, partnerData) => 
    api.put(`/partners/${id}`, partnerData),
  delete: (id) => 
    api.delete(`/partners/${id}`)
};

export default api;